import React, { useState, useRef } from 'react';
import { RotateCcw, ZoomIn, ZoomOut, Eye, Download, Settings } from 'lucide-react';

interface ThreeDViewerProps {
  src: string;
  title: string;
}

const ThreeDViewer: React.FC<ThreeDViewerProps> = ({ src, title }) => {
  const [isRotating, setIsRotating] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [wireframe, setWireframe] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(3, prev + 0.25));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.25, prev - 0.25));
  };

  const resetView = () => {
    setZoom(1);
    setIsRotating(true);
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-900">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-gray-800 text-white">
        <div className="flex items-center gap-3">
          <Eye size={20} />
          <span className="font-medium truncate">{title}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Settings size={16} />
          </button>
          <button
            onClick={() => window.open(src, '_blank')}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Download size={16} />
          </button>
        </div>
      </div>

      {/* 3D Viewer */}
      <div className="flex-1 relative">
        <div 
          ref={viewerRef}
          className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900"
          style={{ transform: `scale(${zoom})` }}
        >
          {/* 3D Model Placeholder */}
          <div className={`relative w-80 h-80 ${isRotating ? 'animate-spin' : ''}`} style={{ animationDuration: '20s' }}>
            <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 rounded-xl shadow-2xl flex items-center justify-center">
              <div className="text-white text-center">
                <div className="text-6xl mb-4">🎨</div>
                <h3 className="text-xl font-bold mb-2">Modelo 3D</h3>
                <p className="text-blue-100">Vista interactiva</p>
              </div>
            </div>
            
            {/* Wireframe Overlay */}
            {wireframe && (
              <div className="absolute inset-0 border-2 border-green-400 rounded-xl">
                <div className="w-full h-full border border-green-400 rounded-xl opacity-50">
                  <div className="grid grid-cols-4 grid-rows-4 w-full h-full">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div key={i} className="border border-green-400 opacity-30" />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-90 rounded-lg p-4 text-white min-w-48">
            <h4 className="font-medium mb-3">Configuración del Visor</h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Rotación automática</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isRotating}
                    onChange={(e) => setIsRotating(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Vista wireframe</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wireframe}
                    onChange={(e) => setWireframe(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between p-4 bg-gray-800 text-white">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-sm min-w-16 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ZoomIn size={16} />
            </button>
          </div>

          <button
            onClick={resetView}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-700 rounded-lg transition-colors text-sm"
          >
            <RotateCcw size={16} />
            Resetear vista
          </button>
        </div>

        <div className="text-sm text-gray-300">
          🖱️ Arrastra para rotar • 🔍 Scroll para zoom • ⌨️ Controles disponibles
        </div>
      </div>

      {/* Instructions */}
      <div className="p-3 bg-gray-700 text-center">
        <p className="text-sm text-gray-300">
          <strong>Controles:</strong> Clic izquierdo para rotar • Scroll para zoom • Clic derecho para mover
        </p>
      </div>
    </div>
  );
};

export default ThreeDViewer;